import React from 'react';
import { formatDublinDateTime, formatDublinDate } from '../utils/date';
import { EnvelopeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  useGetStudentOnboardingQuery,
  useResendStudentOnboardingMutation,
} from '../store/api/localApi';
import { useToast } from './Toast';

interface Props {
  studentId: string;
}

/**
 * Admin-only block that surfaces the parent's pre-completion onboarding
 * state for a student and offers a single non-destructive action:
 *
 *   - Resend : re-sends the relevant email (signup or form-fill).
 *              Smart: re-uses the existing JWT if it hasn't expired,
 *              issues a new one only if needed.
 *
 * Renders nothing once the registration is complete.
 */
const ParentOnboardingCard: React.FC<Props> = ({ studentId }) => {
  const { showSuccess, showError } = useToast();
  const { data, isLoading, error } = useGetStudentOnboardingQuery(studentId);
  const [resend, { isLoading: isResending }] = useResendStudentOnboardingMutation();

  // Loading state — render a skeleton so the page doesn't jump
  // when the query resolves.
  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg" data-testid="parent-onboarding-card-loading">
        <div className="px-4 py-5 sm:p-6">
          <div className="h-4 w-40 bg-gray-100 rounded mb-3" />
          <div className="h-3 w-56 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  // 403 / 404 / other errors: don't surface an error card here. The handler
  // returns 400 NO_PARENT_ON_FILE for students with no parent, which is a
  // legitimate empty-state — admins shouldn't see anything in that case.
  if (error || !data) {
    return null;
  }

  // Onboarding complete + nothing pending: nothing to show.
  if (data.stage === 'complete') {
    return null;
  }

  const handleResend = async () => {
    try {
      const res = await resend(studentId).unwrap();
      const what =
        res.signupAction !== 'skipped' && res.formInvitationResent
          ? 'Resent signup and form-fill emails'
          : res.signupAction !== 'skipped'
          ? `Signup email ${res.signupAction === 'resent' ? 'resent' : 'reissued'}`
          : res.formInvitationCreated
          ? 'Fresh registration form issued'
          : 'Resent form-fill email';
      showSuccess(what);
    } catch (err: any) {
      const code = err?.data?.error;
      const msg = err?.data?.message || 'Failed to resend invite';
      showError(code === 'ONBOARDING_COMPLETE' ? 'Nothing pending to resend' : msg);
    }
  };

  const stageLabel =
    data.stage === 'signup-pending'
      ? 'Signup pending'
      : data.stage === 'needs-invitation'
      ? 'No invitation'
      : 'Form pending';
  const stageDescription =
    data.stage === 'signup-pending'
      ? "Parent hasn't created their account yet."
      : data.stage === 'needs-invitation'
      ? 'Registration is still pending, but no active invitation is on file. Resend to issue a fresh one.'
      : "Parent has signed up but hasn't filled the registration form.";

  return (
    <div className="bg-white shadow rounded-lg" data-testid="parent-onboarding-card">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Parent onboarding</h3>
            <p className="text-xs text-gray-500 mt-0.5">{stageDescription}</p>
          </div>
          <span
            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800"
            data-testid="parent-onboarding-stage"
          >
            <ExclamationTriangleIcon className="h-3.5 w-3.5" />
            {stageLabel}
          </span>
        </div>

        {data.signupInvite && (
          <dl className="text-xs text-gray-600 space-y-1 mb-4">
            <div className="flex justify-between">
              <dt>Invite expires</dt>
              <dd className="font-medium text-gray-900">
                {formatDublinDate(data.signupInvite.expiresAt)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt>Last sent</dt>
              <dd className="font-medium text-gray-900">
                {formatDublinDateTime(data.signupInvite.lastSentAt)}
              </dd>
            </div>
          </dl>
        )}

        {data.formInvitation && data.stage === 'form-pending' && (
          <dl className="text-xs text-gray-600 space-y-1 mb-4">
            <div className="flex justify-between">
              <dt>Form</dt>
              <dd className="font-medium text-gray-900 truncate ml-2">
                {data.formInvitation.formTitle}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt>Sent</dt>
              <dd className="font-medium text-gray-900">
                {formatDublinDateTime(data.formInvitation.sentAt)}
              </dd>
            </div>
          </dl>
        )}

        <button
          type="button"
          onClick={handleResend}
          disabled={isResending}
          data-testid="parent-onboarding-resend-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          <EnvelopeIcon className="h-4 w-4" />
          {isResending ? 'Sending…' : 'Resend invite'}
        </button>
      </div>
    </div>
  );
};

export default ParentOnboardingCard;
